import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ApiError } from "@/api/client";
import {
  useConnection,
  useConnectionSchema,
  useDeleteConnection,
  useKnowledge,
  useRefreshSchema,
  useEvalSuites,
  useRelationships,
  useSourceTypes,
  useTestConnection,
  useUpdateConnection,
  useIsAdmin,
} from "@/api/queries";
import { BusinessMemoryPanel } from "@/components/sources/BusinessMemoryPanel";
import { DiagnosticsReport } from "@/components/sources/DiagnosticsReport";
import { HealthCheckPanel } from "@/components/sources/HealthCheckPanel";
import { ImportPanel } from "@/components/sources/ImportPanel";
import { KnowledgeEditor } from "@/components/sources/KnowledgeEditor";
import { RelationshipsPanel } from "@/components/sources/RelationshipsPanel";
import { SchemaBrowser } from "@/components/sources/SchemaBrowser";
import { UndocumentedNotice } from "@/components/sources/UndocumentedNotice";
import { SourceForm } from "@/components/sources/SourceForm";
import {
  fromConnection,
  toPayload,
  type SourceFormValue,
} from "@/components/sources/formState";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Chip, StatusDot } from "@/components/ui/Chip";
import { Spinner } from "@/components/ui/Spinner";
import {
  BackIcon,
  BookIcon,
  CodeIcon,
  LinkIcon,
  PlugIcon,
  RefreshIcon,
  SettingsIcon,
  ShieldIcon,
  TableIcon,
  TrashIcon,
  UploadIcon,
} from "@/components/icons";
import { cn } from "@/lib/cn";
import { SourceFileCard } from "@/components/sources/SourceFileCard";

export type SourceTab =
  | "schema"
  | "knowledge"
  | "relationships"
  | "health"
  | "files"
  | "settings";

const TABS: Array<{ key: SourceTab; label: string; icon: typeof TableIcon }> = [
  { key: "schema", label: "Schema", icon: TableIcon },
  { key: "knowledge", label: "Knowledge", icon: BookIcon },
  // Beside the knowledge it extends: the dictionary says what this source's
  // columns mean, a relationship says which of them match another source's
  // (PRD FR-1.9). Readable by every role, editable by Admins.
  { key: "relationships", label: "Relationships", icon: LinkIcon },
  // Between the knowledge a source carries and the settings that reach it: a
  // health check is the acceptance test you run once the first two are right
  // (PRD FR-1.8).
  { key: "health", label: "Health", icon: ShieldIcon },
  { key: "files", label: "File", icon: UploadIcon },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  return new Date(iso).toLocaleString();
}

export function SourceDetailPage() {
  const { connectionId } = useParams({ from: "/app/sources/$connectionId" });
  const { tab, relation } = useSearch({ from: "/app/sources/$connectionId" });
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const { data: connection, isLoading } = useConnection(connectionId);
  const { data: sourceTypes } = useSourceTypes();
  const schema = useConnectionSchema(connectionId);
  const knowledge = useKnowledge(connectionId);
  const test = useTestConnection(connectionId);
  const refresh = useRefreshSchema(connectionId);
  const update = useUpdateConnection(connectionId);
  const remove = useDeleteConnection();
  const relationships = useRelationships(connectionId);
  // Suites that ask this source stop running once it is gone (MSE-3).
  const suites = useEvalSuites({ enabled: isAdmin });
  const dependentSuites = (suites.data ?? []).filter((suite) =>
    suite.connection_ids.includes(connectionId),
  ).length;

  const [form, setForm] = useState<SourceFormValue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading || !connection) {
    return (
      <div className="mx-auto flex w-full max-w-5xl items-center gap-2 px-4 py-10 sm:px-8">
        <Spinner />
        <span className="type-support text-ink-secondary">Loading source…</span>
      </div>
    );
  }

  // An analyst reads a source — its schema and its knowledge — and does not
  // change it (ACC-2). The tabs that only exist to change it are not offered;
  // the API would refuse them anyway.
  const visibleTabs = TABS.filter(
    (entry) =>
      (entry.key !== "files" || connection.managed) &&
      (isAdmin || (entry.key !== "files" && entry.key !== "settings" && entry.key !== "health")),
  );
  const active: SourceTab =
    tab && visibleTabs.some((entry) => entry.key === tab) ? tab : "schema";
  const editValue = form ?? fromConnection(connection);

  const saveSettings = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    update.mutate(toPayload(editValue, false), {
      onSuccess: () => setForm(null),
      onError: (caught) =>
        setError(caught instanceof ApiError ? caught.message : "The source could not be saved."),
    });
  };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8 sm:py-10">
      <Link
        to="/sources"
        className="type-support text-ink-secondary hover:text-ink inline-flex items-center gap-1.5 motion-fast"
      >
        <BackIcon className="size-4" />
        All sources
      </Link>

      <header className="mt-4 mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="type-h1 text-ink flex flex-wrap items-center gap-3">
            {connection.name}
            <Chip tone="neutral">
              {connection.file ? connection.file.kind_label : connection.source_type}
            </Chip>
            {connection.file ? (
              <Chip tone="accent">uploaded</Chip>
            ) : connection.managed ? (
              <Chip tone="accent">managed</Chip>
            ) : null}
          </h1>
          <p className="type-support text-ink-secondary mt-2">
            {connection.description || "No description."}
          </p>
          <p className="type-caption text-ink-disabled mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1.5">
              <StatusDot
                tone={
                  connection.last_test_ok === false
                    ? "error"
                    : connection.last_test_ok
                      ? "success"
                      : "neutral"
                }
              />
              Tested {relativeTime(connection.last_tested_at)}
            </span>
            <span className="tabular-nums">
              Row cap {connection.row_cap} · timeout {connection.statement_timeout_seconds}s
            </span>
          </p>
          {/* Its own line in the support role: the caption role uppercases, and
              this names actual server settings. */}
          <p className="type-support text-ink-secondary mt-1">
            {connection.read_only_enforcement}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            icon={<PlugIcon className="size-4" />}
            loading={test.isPending}
            onClick={() => test.mutate()}
          >
            Test
          </Button>
          <Button
            icon={<RefreshIcon className="size-4" />}
            loading={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            Refresh schema
          </Button>
          {/* The workbench opens on *this* source rather than on whichever one
              sorts first, and remembers it (backlog item 7). */}
          <Link
            to="/workbench"
            search={{ connection: connection.id }}
            className="border-border text-ink-secondary hover:bg-hover hover:text-ink rounded-element control-md type-label motion-fast inline-flex items-center gap-2 border px-3 transition-colors"
          >
            <CodeIcon className="size-4" />
            Query
          </Link>
        </div>
      </header>

      {test.data ? (
        <div className="mb-6">
          <DiagnosticsReport report={test.data} />
        </div>
      ) : null}

      <div className="mb-6 empty:hidden">
        <UndocumentedNotice connection={connection} />
      </div>

      <nav className="mb-6 flex gap-1 border-b border-border" aria-label="Source sections">
        {visibleTabs.map((entry) => {
          const Icon = entry.icon;
          const selected = entry.key === active;
          return (
            <button
              key={entry.key}
              type="button"
              aria-current={selected ? "page" : undefined}
              onClick={() =>
                void navigate({
                  to: "/sources/$connectionId",
                  params: { connectionId },
                  search: { tab: entry.key },
                })
              }
              className={cn(
                "flex items-center gap-2 border-b-2 px-3 py-2.5 type-label motion-fast",
                selected
                  ? "border-accent text-ink"
                  : "border-transparent text-ink-secondary hover:text-ink",
              )}
            >
              <Icon className="size-4" />
              {entry.label}
            </button>
          );
        })}
      </nav>

      {active === "schema" ? (
        <section>
          {schema.data ? (
            <>
              <p className="type-support text-ink-secondary mb-4">
                {schema.data.status.relation_count} relations ·{" "}
                {schema.data.has_native_comments
                  ? `${schema.data.status.commented_count} carry native comments`
                  : "this engine stores no comments — the Data Dictionary covers it"}{" "}
                · introspected {relativeTime(schema.data.status.refreshed_at)}
                {schema.data.status.server_version ? ` · ${schema.data.status.server_version}` : ""}
              </p>
              {schema.data.status.error ? (
                <p
                  role="alert"
                  className="type-support text-error rounded-element bg-error-subtle mb-4 p-3"
                >
                  {schema.data.status.error}
                </p>
              ) : null}
              <SchemaBrowser schema={schema.data} focus={relation} />
            </>
          ) : schema.isError ? (
            <p role="alert" className="type-support text-error rounded-element bg-error-subtle p-3">
              {schema.error instanceof ApiError
                ? schema.error.message
                : "The schema could not be read."}
            </p>
          ) : (
            <div className="flex items-center gap-2">
              <Spinner />
              <span className="type-support text-ink-secondary">Introspecting…</span>
            </div>
          )}
        </section>
      ) : null}

      {active === "knowledge" ? (
        <section className="flex flex-col gap-8">
          {/* The generator sits above the editor it writes into: an
              administrator looking at an empty dictionary should find the
              button that fills it before the box that expects them to (FR-2.3). */}
          {isAdmin ? <BusinessMemoryPanel connectionId={connectionId} /> : null}
          {knowledge.data ? (
            <KnowledgeEditor
              connectionId={connectionId}
              knowledge={knowledge.data}
              readOnly={!isAdmin}
            />
          ) : (
            <Spinner />
          )}
        </section>
      ) : null}

      {active === "relationships" ? (
        <section>
          <RelationshipsPanel connectionId={connectionId} readOnly={!isAdmin} />
        </section>
      ) : null}

      {active === "health" ? (
        <section>
          <HealthCheckPanel connectionId={connectionId} />
        </section>
      ) : null}

      {active === "files" && connection.managed ? (
        <section>
          {/* An uploaded source *is* its file: it is replaced, not added to
              (FILE-3). A CSV source made empty keeps the import panel. */}
          {connection.file ? (
            <SourceFileCard connection={connection} />
          ) : (
            <ImportPanel connectionId={connectionId} />
          )}
        </section>
      ) : null}

      {active === "settings" && sourceTypes ? (
        <section className="flex flex-col gap-8">
          <SourceForm
            sourceTypes={sourceTypes}
            value={editValue}
            onChange={setForm}
            onSubmit={saveSettings}
            editing
            hasStoredPassword={connection.has_password}
            error={error}
          >
            <Button type="submit" variant="primary" loading={update.isPending}>
              Save changes
            </Button>
            {form ? (
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                Discard
              </Button>
            ) : null}
          </SourceForm>

          <div className="rounded-container border border-error bg-error-subtle p-4">
            <h3 className="type-h4 text-ink">Remove this source</h3>
            <p className="type-support text-ink-secondary mt-1">
              {connection.file
                ? "Removing the source deletes the uploaded file and every version of it from the files volume."
                : connection.managed
                ? "This source’s database was created here, so removing the source deletes the imported data with it."
                : "The database itself is left untouched; only the connection to it is removed."}{" "}
              The SQL audit trail is kept.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                icon={<TrashIcon className="size-4" />}
                onClick={() => setConfirmDelete(true)}
              >
                Remove source
              </Button>
            </div>
            <ConfirmDialog
              open={confirmDelete}
              onClose={() => setConfirmDelete(false)}
              title={`Remove ${connection.name}?`}
              description={
                connection.file
                  ? `${connection.name} and its uploaded file, ${connection.file.original_name}.`
                  : connection.managed
                  ? `The connection to ${connection.name} and the data imported into it.`
                  : `The connection to ${connection.name}. The database itself is left untouched.`
              }
              consequence={removalConsequence(relationships.data?.length ?? 0, dependentSuites)}
              confirmLabel={`Remove ${connection.name}`}
              loading={remove.isPending}
              onConfirm={() =>
                remove.mutate(connectionId, {
                  onSuccess: () => void navigate({ to: "/sources" }),
                })
              }
            />
          </div>
        </section>
      ) : null}
    </div>
  );
}

/** What goes with a source: its declarations and the eval suites that ask it,
 * which nothing else would mention. */
function removalConsequence(relationships: number, suites: number): string {
  const declared =
    relationships === 0
      ? ""
      : relationships === 1
        ? " Its 1 declared relationship with another source is removed too."
        : ` Its ${relationships} declared relationships with other sources are removed too.`;
  const measured =
    suites === 0
      ? ""
      : suites === 1
        ? " 1 eval suite asks it and will stop running; its history is kept."
        : ` ${suites} eval suites ask it and will stop running; their history is kept.`;
  return `Conversations on this source can no longer ask new questions of it.${declared}${measured} The SQL audit trail is kept.`;
}
